import React, { useState } from 'react';

function Tooltip({ texto }) {
  const [visivel, setVisivel] = useState(false);
  if (!texto) return null;

  return (
    <span className="relative inline-flex ml-1.5 align-middle">
      <button
        type="button"
        onMouseEnter={() => setVisivel(true)}
        onMouseLeave={() => setVisivel(false)}
        onFocus={() => setVisivel(true)}
        onBlur={() => setVisivel(false)}
        className="w-4 h-4 rounded-full bg-brand-100 text-brand-600 text-xs font-bold flex items-center justify-center hover:bg-brand-200 transition-colors focus:outline-none"
        aria-label="Mais informações"
      >
        ?
      </button>
      {visivel && (
        <div className="absolute left-6 top-0 z-50 w-72 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl leading-relaxed pointer-events-none">
          {texto}
          <div className="absolute left-[-4px] top-2 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
    </span>
  );
}

function Recomendacao({ texto }) {
  if (!texto) return null;
  return (
    <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
      <svg className="w-3 h-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <span><strong>Sugestão:</strong> {texto}</span>
    </p>
  );
}

function NumeroBadge({ numero }) {
  return (
    <span className="w-5 h-5 rounded-full bg-gray-300 text-gray-600 text-xs font-bold flex items-center justify-center shrink-0 leading-none select-none">
      {numero}
    </span>
  );
}

export default function ModuloItem({ item, valor, onChange, readOnly, index = 0 }) {
  const bgBase = index % 2 === 0 ? 'bg-white' : 'bg-slate-50';

  if (item.tipo === 'bool') {
    const ativo = valor === true;
    return (
      <label className={`flex items-center gap-2 px-2 py-1.5 rounded border transition-all cursor-pointer ${
        readOnly ? 'opacity-70 cursor-default' : 'hover:border-brand-300 hover:bg-brand-50'
      } ${ativo ? 'border-brand-400 bg-brand-50' : `border-gray-200 ${bgBase}`}`}>
        <NumeroBadge numero={index + 1} />
        <input
          type="checkbox"
          checked={!!valor}
          onChange={e => !readOnly && onChange(e.target.checked)}
          disabled={readOnly}
          className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-400 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 flex items-center flex-wrap gap-0.5">
            {item.label}
            <Tooltip texto={item.descricao} />
            {(valor === null || valor === undefined)
              ? <span className="text-xs text-amber-600 font-normal ml-1">— Não respondido</span>
              : <span className="text-xs text-green-600 font-normal ml-1">— {valor ? 'Sim' : 'Não'}</span>
            }
          </p>
          <Recomendacao texto={item.recomendacao} />
        </div>
      </label>
    );
  }

  if (item.tipo === 'opcoes') {
    const respondido = valor !== null && valor !== undefined;
    return (
      <div className={`px-2 py-1.5 rounded border ${respondido ? 'border-brand-400 bg-brand-50' : `border-gray-200 ${bgBase}`}`}>
        <p className="text-sm font-semibold text-gray-900 mb-1 flex items-center flex-wrap gap-1">
          <NumeroBadge numero={index + 1} />
          {item.label}
          <Tooltip texto={item.descricao} />
          {!respondido &&
            <span className="text-xs text-amber-600 font-normal ml-1">— Não respondido</span>
          }
        </p>
        <div className="flex flex-wrap gap-1.5 pl-6">
          {(item.opcoes || []).map(op => (
            <label key={op.valor} className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer transition-all ${
              valor === op.valor
                ? 'border-brand-600 bg-brand-600 text-white font-medium'
                : readOnly
                  ? 'border-gray-200 text-gray-500 cursor-default'
                  : 'border-gray-200 text-gray-700 hover:border-brand-400 hover:bg-brand-50'
            }`}>
              <input
                type="radio"
                name={item.id}
                value={op.valor}
                checked={valor === op.valor}
                onChange={() => !readOnly && onChange(op.valor)}
                disabled={readOnly}
                className="sr-only"
              />
              {op.label}
            </label>
          ))}
        </div>
        <Recomendacao texto={item.recomendacao} />
      </div>
    );
  }

  if (item.tipo === 'texto') {
    return (
      <div className={`px-2 py-1.5 rounded border border-gray-200 ${bgBase}`}>
        <label className="text-sm font-semibold text-gray-900 flex items-center gap-1 mb-1">
          <NumeroBadge numero={index + 1} />
          {item.label}
          <Tooltip texto={item.descricao} />
          {!valor && <span className="text-xs text-amber-600 font-normal ml-1">— Não respondido</span>}
        </label>
        <div className="pl-6">
          <input
            type="text"
            value={valor || ''}
            onChange={e => !readOnly && onChange(e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="Digite aqui..."
          />
        </div>
        <Recomendacao texto={item.recomendacao} />
      </div>
    );
  }

  if (item.tipo === 'numerico') {
    return (
      <div className={`px-2 py-1.5 rounded border border-gray-200 ${bgBase}`}>
        <label className="text-sm font-semibold text-gray-900 flex items-center gap-1 mb-1">
          <NumeroBadge numero={index + 1} />
          {item.label}
          <Tooltip texto={item.descricao} />
          {(valor === null || valor === undefined) &&
            <span className="text-xs text-amber-600 font-normal ml-1">— Não respondido</span>
          }
        </label>
        <div className="flex items-center gap-2 pl-6">
          <input
            type="number"
            value={valor ?? ''}
            onChange={e => !readOnly && onChange(e.target.value === '' ? null : Number(e.target.value))}
            disabled={readOnly}
            className="input w-32"
            placeholder="0"
            min="0"
          />
          {item.unidade && (
            <span className="text-sm text-gray-500">{item.unidade}</span>
          )}
        </div>
        <Recomendacao texto={item.recomendacao} />
      </div>
    );
  }

  if (item.tipo === 'faixa') {
    const de = valor?.de ?? '';
    const ate = valor?.ate ?? '';
    const respondido = valor?.de !== null && valor?.de !== undefined && valor?.ate !== null && valor?.ate !== undefined;
    return (
      <div className={`px-2 py-1.5 rounded border border-gray-200 ${bgBase}`}>
        <label className="text-sm font-semibold text-gray-900 flex items-center gap-1 mb-1">
          <NumeroBadge numero={index + 1} />
          {item.label}
          <Tooltip texto={item.descricao} />
          {!respondido && <span className="text-xs text-amber-600 font-normal ml-1">— Não respondido</span>}
        </label>
        <div className="flex items-center gap-2 pl-6">
          <input
            type="number"
            value={de}
            onChange={e => !readOnly && onChange({ ...valor, de: e.target.value === '' ? null : Number(e.target.value) })}
            disabled={readOnly}
            className="input w-28"
            placeholder="De"
            min="0"
          />
          <span className="text-sm text-gray-400">até</span>
          <input
            type="number"
            value={ate}
            onChange={e => !readOnly && onChange({ ...valor, ate: e.target.value === '' ? null : Number(e.target.value) })}
            disabled={readOnly}
            className="input w-28"
            placeholder="Até"
            min="0"
          />
          {item.unidade && (
            <span className="text-sm text-gray-500">{item.unidade}</span>
          )}
        </div>
        <Recomendacao texto={item.recomendacao} />
      </div>
    );
  }

  return null;
}
